import { Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Import new client logos (WebP for better performance)
import logoMariadaModa from "@/assets/inspiration/logo-mariadamoda.webp";
import logoBazarMisharia from "@/assets/inspiration/logo-bazarmisharia.webp";
import logoOdontoDavid from "@/assets/inspiration/logo-odontodavid.webp";
import logoCabeleireiraLeila from "@/assets/inspiration/logo-cabeleireiraleila.webp";
import logoPriscilinhaJoias from "@/assets/inspiration/logo-priscilinhajoias.webp";
import logoFlipMellow from "@/assets/inspiration/logo-flipmellow.webp";
import logoBrutalBurger from "@/assets/inspiration/logo-brutalburger.webp";
import logoEnfoqueConsultoria from "@/assets/inspiration/logo-enfoqueconsultoria.webp";
import logoAugustyleSurfwear from "@/assets/inspiration/logo-augustylesurfwear.webp";

const createdBrands = [
  { name: "Maria da Moda", location: "Feira de Santana, BA", segment: "Moda Feminina", emoji: "👗", image: logoMariadaModa, isRecent: true },
  { name: "Odonto David", location: "Vitória, ES", segment: "Saúde e Bem-estar", emoji: "🦷", image: logoOdontoDavid, isRecent: true },
  { name: "Brutal Burger", location: "Amargosa, BA", segment: "Alimentação", emoji: "🍔", image: logoBrutalBurger, isRecent: true },
  { name: "Priscilinha Jóias", location: "Portugal", segment: "E-Commerce", emoji: "💎", image: logoPriscilinhaJoias, isRecent: false },
  { name: "Cabeleireira Leila", location: "Curitiba, PR", segment: "Beleza e Estética", emoji: "💇", image: logoCabeleireiraLeila, isRecent: false },
  { name: "Flip Mellow", location: "Irecê, BA", segment: "Moda Masculina", emoji: "🛹", image: logoFlipMellow, isRecent: false },
  { name: "Bazar Misharia", location: "Belo Horizonte, MG", segment: "Moda Feminina", emoji: "👚", image: logoBazarMisharia, isRecent: false },
  { name: "Enfoque Consultoria", location: "Maringá, PR", segment: "Imóveis", emoji: "🏠", image: logoEnfoqueConsultoria, isRecent: false },
  { name: "Augustyle Surfwear", location: "Brasil", segment: "Moda Surf", emoji: "🏄", image: logoAugustyleSurfwear, isRecent: false }
];

export const LogoGallery = () => {
  return (
    <section className="py-16 sm:py-20 lg:py-28 bg-muted/20 content-below-fold">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-2">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">Feed de Criações</span>
          </div>
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-display font-bold text-foreground mb-1 text-balance">
            🔥 Marcas Criadas com ClickLogos 🔥
          </h2>
          <p className="subtitle-section max-w-2xl mx-auto">
            Veja o que outros empreendedores brasileiros estão criando
          </p>
        </div>

        {/* Logo Grid */}
        <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-2 sm:pb-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 max-w-6xl mx-auto scrollbar-hide justify-start sm:justify-center">
          {createdBrands.map((brand, index) => (
            <div 
              key={index}
              className="flex-shrink-0 w-[80vw] max-w-[320px] sm:w-auto sm:max-w-none snap-center group bg-card rounded-2xl border border-border/40 hover:border-primary/30 shadow-premium hover:shadow-elevated transition-all duration-300 overflow-hidden animate-fade-in active:scale-[0.98]"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Header */}
              <div className="p-4 border-b border-border/30">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {brand.isRecent && (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                      )}
                      <h3 className="font-semibold text-foreground text-lg">
                        {brand.name}
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {brand.location}
                    </p>
                  </div>
                  {brand.isRecent && (
                    <Badge className="bg-green-900/20 text-green-900 dark:text-green-200 border-green-900/30 animate-pulse">
                      NOVO
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Logo Image Area - All lazy to not compete with LCP */}
              <div className="aspect-[4/3] sm:aspect-square p-6 sm:p-10 flex items-center justify-center">
                <img 
                  src={brand.image} 
                  alt={`${brand.name} logo`}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  width={280}
                  height={280}
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                />
              </div>
              
              {/* Footer */}
              <div className="p-4 border-t border-border/30">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{brand.emoji}</span>
                  <span className="text-sm font-medium text-foreground">
                    {brand.segment}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
